import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, CartesianGrid } from "recharts";

const TOOLTIP_STYLE = { borderRadius: 6, border: "1px solid #e2e8f0" };
const BAR_RADIUS = [4, 4, 0, 0];
const LEGEND_STYLE = { fontSize: 11 };
const DOT_STYLE = { r: 3 };

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      const [ov, mo] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/analytics/monthly-returns"),
      ]);
      if (ignore) return;
      setStats(ov.data);
      setMonthly(mo.data);
    };
    load();
    return () => { ignore = true; };
  }, []);

  const typeData = useMemo(
    () => (stats ? Object.entries(stats.by_type || {}).map(([k, v]) => ({ type: k, count: v })) : []),
    [stats],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="analytics-title">Analytics & Reports</h1>
        <p className="text-slate-600 mt-1">Deep insight into filing volume, tax liability, and refunds.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Total tax liability</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#0A2540]">${(stats?.total_liability || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Refunds delivered</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-emerald-700">${(stats?.total_refund || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Open notices</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#D32F2F]">{stats?.open_notices ?? 0}</div>
        </div>
      </div>

      <div className="rounded-md border tf-hairline bg-white p-6">
        <h3 className="font-[Chivo] font-bold text-[#0A2540]">Liability vs Refunds by month</h3>
        <div className="mt-4 h-72" data-testid="chart-liability">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend iconSize={10} wrapperStyle={LEGEND_STYLE} />
              <Line type="monotone" dataKey="liability" stroke="#0A2540" strokeWidth={2} dot={DOT_STYLE} />
              <Line type="monotone" dataKey="refunds" stroke="#16a34a" strokeWidth={2} dot={DOT_STYLE} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-md border tf-hairline bg-white p-6">
        <h3 className="font-[Chivo] font-bold text-[#0A2540]">Filings by return type</h3>
        <div className="mt-4 h-72" data-testid="chart-types">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={typeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="type" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#D32F2F" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
